// Frontend translation helper with an in-memory cache.
// Pairs with the backend /api/translate/batch endpoint, which also caches
// indefinitely in Mongo. The in-memory cache here is just to coalesce
// duplicate calls during a single browsing session.
import axios from "axios";
import { API } from "@/AppContext";

const CACHE = new Map(); // key: `${lang}::${text}` -> translated
const INFLIGHT = new Map(); // key: same -> Promise resolving translated

// Cheap Spanish-signal detection (mirrors the backend). English is the app's
// base language, so when the viewer wants English we only call the translator
// for text that actually looks Spanish. This keeps English pages (and the
// social-bot prerender that runs as lang='en') call-free — avoiding both the
// Facebook "418" prerender hang and needless LLM spend — while still
// translating genuine Spanish jornalero content for English viewers.
const _ES_STOPWORDS = new Set([
  "el","la","los","las","un","una","unos","unas","de","del","y","o","que","en",
  "por","para","con","sin","es","son","soy","estoy","esta","este","esto","se",
  "su","sus","mi","tu","no","si","muy","mas","pero","como","porque","cuando",
  "donde","quien","hay","tengo","tiene","necesito","busco","trabajo","chamba",
  "patron","troka","obra","albanil","jardineria","limpieza","experiencia","anos",
  "hola","gracias","buenas","dias","disponible","ciudad","casa","pago",
]);
const _ES_CHARS = /[áéíóúüñ¿¡]/i;

function looksSpanish(text) {
  if (_ES_CHARS.test(text)) return true;
  const tokens = (text.toLowerCase().match(/[a-záéíóúüñ]+/g)) || [];
  if (tokens.length === 0) return false;
  let hits = 0;
  for (const tok of tokens) if (_ES_STOPWORDS.has(tok)) hits++;
  return hits >= 2 || (hits >= 1 && tokens.length <= 4);
}

// True when this text needs no network translate call for the given lang.
// We translate ONLY for a real visitor who has switched to Spanish. English is
// the base language (always a no-op), and crawlers / the social-bot prerender
// run as the default 'en' (no saved language) so they never trigger a call.
function isNoop(lang, text) {
  if (lang !== "es") return true;       // only opted-in Spanish users get translation
  if (looksSpanish(text)) return true;  // already Spanish, nothing to do
  return false;
}

function cacheKey(lang, text) {
  return `${lang}::${text}`;
}

/** Translate a single text. Returns the translated string (or the original on failure). */
export async function translateOne(lang, text, headers) {
  if (!text || text.length < 2) return text;
  if (isNoop(lang, text)) return text;
  return _doTranslate(lang, text, headers);
}

// Decide whether a FORCED translation (user tapped "Translate") actually needs a
// network call — skips text already in the target language so we never make a
// wasteful English->English (or Spanish->Spanish) call.
function shouldForceTranslate(target, text) {
  if (target === "es") return !looksSpanish(text); // only translate English-looking text
  if (target === "en") return looksSpanish(text);  // only translate Spanish-looking text
  return true;
}

/** True if `text` is in a language different from `target` (drives the auto prompt). */
export function needsForeignTranslation(target, text) {
  if (!text || text.trim().length < 4) return false;
  return shouldForceTranslate(target, text);
}

/** Force-translate one text to `target` on demand, bypassing the opt-in guard
 * but still skipping text already in the target language. */
export async function translateOneForce(target, text, headers) {
  if (!text || text.length < 2) return text;
  if (!shouldForceTranslate(target, text)) return text;
  return _doTranslate(target, text, headers);
}

async function _doTranslate(lang, text, headers) {
  const k = cacheKey(lang, text);
  if (CACHE.has(k)) return CACHE.get(k);
  if (INFLIGHT.has(k)) return INFLIGHT.get(k);
  const p = (async () => {
    try {
      const r = await axios.post(
        `${API}/translate/batch`,
        { target_lang: lang, items: [{ id: "0", text }] },
        { headers, timeout: 15000 }
      );
      const tt = r?.data?.translations?.["0"] || text;
      CACHE.set(k, tt);
      return tt;
    } catch {
      return text;
    } finally {
      INFLIGHT.delete(k);
    }
  })();
  INFLIGHT.set(k, p);
  return p;
}

/** Translate a list of {id, text}. Returns {id: translated}. */
export async function translateBatch(lang, items, headers) {
  const result = {};
  const needed = [];
  for (const it of items) {
    if (!it?.text || it.text.length < 2 || isNoop(lang, it.text)) {
      result[it.id] = it?.text || "";
      continue;
    }
    const k = cacheKey(lang, it.text);
    if (CACHE.has(k)) {
      result[it.id] = CACHE.get(k);
    } else {
      needed.push(it);
    }
  }
  if (needed.length === 0) return result;
  try {
    const r = await axios.post(
      `${API}/translate/batch`,
      { target_lang: lang, items: needed.map((it) => ({ id: String(it.id), text: it.text })) },
      { headers, timeout: 20000 }
    );
    const tr = r?.data?.translations || {};
    for (const it of needed) {
      const t = tr[String(it.id)] ?? it.text;
      result[it.id] = t;
      CACHE.set(cacheKey(lang, it.text), t);
    }
  } catch {
    for (const it of needed) result[it.id] = it.text;
  }
  return result;
}

export function clearTranslationCache() {
  CACHE.clear();
  INFLIGHT.clear();
}

/** True while any translation request is still in flight (for "please wait" UI). */
export function hasInflightTranslations() {
  return INFLIGHT.size > 0;
}
